// La barra flotante de UNA celda — ADR-2026-09-16-la-barra-del-lienzo-muestra-carrusel-y-animar-2.
//
// A la vista, lo que uno hace con una foto recién salida: Agregar al chat · Ver ·
// Verificar (con la verificación de calidad prendida y la foto sin analizar,
// ADR-2026-09-21-verificar-va-en-la-barra-de-la-celda) · Carrusel · Animar · ⋯ ·
// Descargar · Enviar a lanzamiento (con la función prendida, siempre al final). Descargar está SIEMPRE, también con lanzamientos (en el inline lo
// reemplaza; acá conviven). El "⋯" despliega un menú vertical con el mismo estilo con
// las herramientas del visor: Mejorar resolución, Expansión mágica, Recortar, Marca de
// agua, Descripción, y las dos pantallas (Canva, Anuncio). Un video no tiene nada de
// eso: le quedan Agregar · Ver · Corregir voz · Editar con Estudio · Descargar y ningún
// menú (ADR-2026-09-24-corregir-voz-en-la-barra-del-lienzo,
// ADR-2026-09-24-editar-con-estudio-en-la-barra-del-lienzo).
//
// Cada herramienta abre AHÍ MISMO su panel (`abrirHerramienta`), sin pasar por el visor
// (ADR-2026-09-16-las-herramientas-del-lienzo-abren-en-panel); "Ver" es lo único que
// abre el visor. Sin "Editar": Matías lo sacó el 2026-09-16 (el editor sigue a mano
// desde "Ver").
//
// La barra entra y sale con una transición corta (opacidad + un asomo desde la foto):
// `animada` lo maneja quien la monta, que la deja montada mientras dura la salida.
//
// `nodrag nopan`: React Flow le pasa el pointer a la barra como si fuera el nodo; sin
// esas clases un click en un botón arrancaba un arrastre.

import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpFromLine,
  AudioLines,
  Clapperboard,
  Copy,
  Crop,
  Download,
  Expand,
  Eye,
  Instagram,
  Loader2,
  Maximize,
  MessageSquarePlus,
  MoreHorizontal,
  Play,
  Shapes,
  ShieldCheck,
  Stamp,
} from 'lucide-react';
import EnviarALanzamiento from '../../components/EnviarALanzamiento';
import { useExitTransition } from '../../hooks/useExitTransition';
import { EXPANSION_DISPONIBLE } from '../../lib/expansionMagica';
import type { AccionVisor } from '../../pages/GeneratedImagesHistory';
import { descargarCreacion } from '../../pages/v2/components/descargarCreacion';
import { usePedirAnalisis } from '../../pages/v2/verificacion/CapaVerificacion';
import {
  adjuntoDeCelda,
  irAEdicionCanva,
  type CeldaParaAccion,
} from './accionesDeCelda';
import type { Herramienta } from './herramientas/herramienta';
import { AYUDA_BORRADOR, borradorVigente, ROTULO_PASAR_A_1080P } from '../../lib/resolucionVideo';
import { LienzoContext } from './lienzoContext';

export const BOTON_DE_BARRA =
  'inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[12.5px] font-semibold text-brand-black transition-colors hover:bg-[#02062B]/[0.06] disabled:opacity-50';
/** Un ítem del menú: la misma letra y el mismo hover que la barra, a lo ancho. Radio 12
 *  dentro de la caja de 16 con 4 de aire (regla concéntrica). */
export const ITEM_DE_MENU =
  'flex h-9 w-full items-center gap-2 whitespace-nowrap rounded-xl px-3 text-left text-[12.5px] font-semibold text-brand-black transition-colors hover:bg-[#02062B]/[0.06]';
/** La caja de la barra y la del menú: mismo borde, fondo y sombra. */
export const CAJA_DE_BARRA = 'border border-[#02062B]/[0.06] bg-white p-1 shadow-[0_6px_20px_rgba(2,6,43,0.12)]';
/** Lo que dura la salida del menú (la entrada dura lo mismo). */
export const MS_MENU = 160;

/** Las herramientas del visor que el "⋯" ofrece, en el orden del menú "Más" del visor. */
const HERRAMIENTAS: {
  accion: AccionVisor;
  rotulo: string;
  Icono: typeof Crop;
  disponible?: boolean;
}[] = [
  { accion: 'mejorar', rotulo: 'Mejorar resolución', Icono: Maximize },
  {
    accion: 'expansion',
    rotulo: 'Expansión mágica',
    Icono: Expand,
    disponible: EXPANSION_DISPONIBLE,
  },
  { accion: 'recortar', rotulo: 'Recortar', Icono: Crop },
  { accion: 'marca-de-agua', rotulo: 'Marca de agua', Icono: Stamp },
  { accion: 'descripcion', rotulo: 'Descripción', Icono: Instagram },
];

export const BarraDeCelda: React.FC<{
  celda: CeldaParaAccion;
  /** `false` mientras entra y mientras sale: la barra se dibuja apagada y corrida. */
  animada: boolean; /** El id del nodo del lienzo: con él se ubica la foto en pantalla al agregarla. */ nodoId?: string }> = ({ celda, animada, nodoId }) => {
  const lienzo = useContext(LienzoContext);
  const navigate = useNavigate();
  const [descargando, setDescargando] = useState(false);
  const [menu, setMenu] = useState(false);
  const { mounted: menuMontado, animated: menuAbierto } = useExitTransition(menu, MS_MENU);
  const raizRef = useRef<HTMLDivElement | null>(null);
  // "Verificar" sólo mientras la foto no tiene análisis (ni en curso): después el
  // resultado vive en la pastilla sobre la foto, con "Volver a analizar" en su popover.
  const { pedir: verificar, modal: modalDeVerificacion } = usePedirAnalisis(lienzo.verificacion, celda.imageId);
  const conVerificar =
    !celda.esVideo && !!lienzo.verificacion && lienzo.verificacion.estadoDe(celda.imageId).tipo === 'boton';

  // La barra se va (deselección): el menú se va con ella.
  useEffect(() => {
    if (!animada) setMenu(false);
  }, [animada]);
  // Un toque fuera de la barra cierra el menú (está en un portal: el click no llega al
  // nodo), y Escape también.
  useEffect(() => {
    if (!menu) return;
    const alTocar = (e: PointerEvent) => {
      if (raizRef.current && !raizRef.current.contains(e.target as Node)) setMenu(false);
    };
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(false);
    };
    document.addEventListener('pointerdown', alTocar, true);
    document.addEventListener('keydown', alTeclado);
    return () => {
      document.removeEventListener('pointerdown', alTocar, true);
      document.removeEventListener('keydown', alTeclado);
    };
  }, [menu]);

  const agregar = () => {
    const chip = adjuntoDeCelda(celda);
    if (!chip) return;
    // Dónde está la foto en pantalla, por si quien recibe la quiere animar hacia su
    // destino (Redes la vuela a "Añadir referencia"). La barra NO vive adentro del nodo
    // (`NodeToolbar` la portalea), así que el nodo se busca por su id.
    const nodo = nodoId ? document.querySelector(`.react-flow__node[data-id="${CSS.escape(nodoId)}"]`) : null;
    lienzo.agregarAdjunto(chip, nodo ? { rect: nodo.getBoundingClientRect(), src: celda.src } : undefined);
  };
  const descargar = async () => {
    setDescargando(true);
    try {
      await descargarCreacion({
        companyId: celda.companyId,
        datos: celda.datos,
        esVideo: celda.esVideo,
        resolverOpciones: lienzo.resolverOpciones,
        aplicarOpciones: lienzo.aplicarOpciones,
      });
    } catch (e) {
      lienzo.avisarError(e instanceof Error ? e.message : 'No se pudo descargar.');
    } finally {
      setDescargando(false);
    }
  };
  const ver = () => {
    setMenu(false);
    lienzo.abrir(celda.imageId);
  };
  const conHerramienta = (herramienta: Herramienta) => {
    setMenu(false);
    lienzo.abrirHerramienta(celda, herramienta);
  };

  return (
    <div
      ref={raizRef}
      className={`nodrag nopan relative origin-bottom transition-[opacity,transform] duration-200 ease-out ${
        animada ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-1.5 scale-95 opacity-0'
      }`}
    >
      <div className={`flex items-center gap-0.5 rounded-full ${CAJA_DE_BARRA}`}>
        <button type="button" onClick={agregar} className={BOTON_DE_BARRA} title={lienzo.rotuloAgregar ?? 'Agregar al chat'}>
          <MessageSquarePlus className="h-4 w-4" aria-hidden="true" />
          {lienzo.rotuloAgregar ?? 'Agregar al chat'}
        </button>
        <button
          type="button"
          onClick={ver}
          className={BOTON_DE_BARRA}
          title="Ver en el visor"
        >
          <Eye className="h-4 w-4" aria-hidden="true" />
          Ver
        </button>
        {celda.esVideo && borradorVigente(celda.datos.datosGeneracion) && (
          <button
            type="button"
            onClick={() => conHerramienta('pasar-a-1080p')}
            className={BOTON_DE_BARRA}
            title={AYUDA_BORRADOR}
          >
            <ArrowUpFromLine className="h-4 w-4" aria-hidden="true" />
            {ROTULO_PASAR_A_1080P}
          </button>
        )}
        {celda.esVideo && (
          <button
            type="button"
            onClick={() => conHerramienta('corregir-voz')}
            className={BOTON_DE_BARRA}
            title="Corregir una palabra mal dicha. No consume créditos."
          >
            <AudioLines className="h-4 w-4" aria-hidden="true" />
            Corregir voz
          </button>
        )}
        {celda.esVideo && (
          <button
            type="button"
            onClick={() => conHerramienta('estudio')}
            className={BOTON_DE_BARRA}
            title="Abrir el video en el Estudio de Video: recortar, sumar voz en off, unir con otros videos"
          >
            <Clapperboard className="h-4 w-4" aria-hidden="true" />
            Editar con Estudio
          </button>
        )}
        {conVerificar && (
          <button
            type="button"
            onClick={() => {
              setMenu(false);
              verificar();
            }}
            className={BOTON_DE_BARRA}
            title="Verificar calidad"
          >
            <ShieldCheck className="h-4 w-4" aria-hidden="true" />
            Verificar
          </button>
        )}
        {!celda.esVideo && (
          <>
            <button
              type="button"
              onClick={() => conHerramienta('carrusel')}
              className={BOTON_DE_BARRA}
              title="Carrusel"
            >
              <Copy className="h-4 w-4" aria-hidden="true" />
              Carrusel
            </button>
            <button
              type="button"
              onClick={() => conHerramienta('animar')}
              className={BOTON_DE_BARRA}
              title="Animar"
            >
              <Play className="h-4 w-4" aria-hidden="true" />
              Animar
            </button>
            <button
              type="button"
              onClick={() => setMenu((v) => !v)}
              aria-expanded={menu}
              aria-haspopup="menu"
              aria-label="Más herramientas"
              title="Más"
              className={`${BOTON_DE_BARRA} w-9 justify-center px-0 ${menu ? 'bg-[#02062B]/[0.06]' : ''}`}
            >
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
            </button>
          </>
        )}
        <button
          type="button"
          onClick={descargar}
          disabled={descargando}
          className={BOTON_DE_BARRA}
          title="Descargar"
        >
          {descargando ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Download className="h-4 w-4" aria-hidden="true" />
          )}
          Descargar
        </button>
        {/* Lanzamiento, a la derecha del todo: el popover se ancla por la derecha para
            no salirse de pantalla. */}
        {!celda.esVideo && lienzo.tiendaConectada && (
          <EnviarALanzamiento
            companyId={celda.companyId}
            brandId={lienzo.brandId}
            imagenes={[
              {
                id: celda.imageId,
                fileName: celda.datos.fileName,
                isVideo: false,
                variantes_ids: celda.datos.variantesIds,
                variante_id: celda.datos.varianteId,
                datos_generacion: (celda.datos.datosGeneracion ?? null) as Record<
                  string,
                  unknown
                > | null,
              },
            ]}
            className={BOTON_DE_BARRA}
            contenedorClassName="shrink-0"
            tamanoIcono="h-4 w-4"
            direccion="abajo"
            alineacion="derecha"
            label="Tienda online"
          />
        )}
      </div>
      {modalDeVerificacion}
      {menuMontado && (
        <div
          role="menu"
          className={`absolute right-0 top-full mt-2 flex min-w-[196px] origin-top-right flex-col gap-0.5 rounded-2xl transition-[opacity,transform] duration-150 ease-out ${CAJA_DE_BARRA} ${
            menuAbierto ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          }`}
        >
          {HERRAMIENTAS.filter((h) => h.disponible !== false).map(({ accion, rotulo, Icono }) => (
            <button
              key={accion}
              type="button"
              role="menuitem"
              onClick={() => conHerramienta(accion)}
              className={ITEM_DE_MENU}
              title={rotulo}
            >
              <Icono className="h-4 w-4" aria-hidden="true" />
              {rotulo}
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            onClick={() => irAEdicionCanva(navigate, celda)}
            className={ITEM_DE_MENU}
            title="Edición Canva"
          >
            <Shapes className="h-4 w-4" aria-hidden="true" />
            Canva
          </button>
        </div>
      )}
    </div>
  );
};
